import { useEffect, useMemo, useState } from 'react';
import { resolveAreaIntelligence } from './config';
import {
  AMENITY_CATEGORIES,
  computeDriveRoute,
  findNearby,
  resolvePlaceCoords,
} from './googleClient';
import { buildStreetViewPreviewUrl } from './googleProvider';

/**
 * Loads live area intelligence for an origin coordinate.
 *
 * status: 'off' (no key — platform mode), 'loading', 'live' (>=1 category
 * returned), 'unavailable' (key present but every Google call failed).
 * amenities[cat.key] = array (may be empty = none within radius) | null (call failed).
 * commutes = [{ base, durationText, distanceKm }] for resolvable bases.
 */
export function useAreaIntelligence(origin, baseTargets = [], { compareAll = false } = {}) {
  const intel = resolveAreaIntelligence();
  const [state, setState] = useState({ status: 'loading', amenities: {}, commutes: [], streetViewUrl: null });
  const basesKey = baseTargets.map((b) => b.name).join('|');

  const originOk = origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng);

  useEffect(() => {
    let cancelled = false;
    if (!originOk || intel.mode !== 'google') {
      setState({ status: 'off', amenities: {}, commutes: [], streetViewUrl: null });
      return undefined;
    }
    setState((s) => ({ ...s, status: 'loading' }));

    (async () => {
      const targetBases = compareAll ? baseTargets.slice(0, 3) : baseTargets.slice(0, 1);
      const [amenityResults, commuteResults] = await Promise.all([
        Promise.all(AMENITY_CATEGORIES.map((cat) => findNearby(origin, cat))),
        Promise.all(
          targetBases.map(async (target) => {
            // Platform base-registry coordinates first; Google text search only
            // as a fallback for bases the registry does not know.
            const coords = Number.isFinite(target.lat)
              ? { lat: target.lat, lng: target.lng }
              : await resolvePlaceCoords(target.name, origin);
            if (!coords) return null;
            const route = await computeDriveRoute(origin, coords);
            return route ? { base: target.name, ...route } : null;
          }),
        ),
      ]);
      if (cancelled) return;

      const amenities = {};
      AMENITY_CATEGORIES.forEach((cat, i) => {
        amenities[cat.key] = amenityResults[i];
      });
      const anyLive =
        amenityResults.some((r) => Array.isArray(r)) || commuteResults.some(Boolean);
      setState({
        status: anyLive ? 'live' : 'unavailable',
        amenities,
        commutes: commuteResults.filter(Boolean),
        streetViewUrl: buildStreetViewPreviewUrl(origin),
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originOk, origin?.lat, origin?.lng, basesKey, compareAll, intel.mode]);

  return useMemo(() => ({ ...state, mode: intel.mode }), [state, intel.mode]);
}
